import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Instagram, ArrowRight, PlayCircle } from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * iter 108 - /about
 *
 * Public marketing page. Cream Mundalo aesthetic, Cormorant serif headers,
 * Hemingway voice. Two founder cards, intro video, bottom CTA.
 *
 * Photos + video are hosted as Emergent artifacts; URLs are stable.
 */

const FRANCISCO_PHOTO = "https://customer-assets.emergentagent.com/job_flamboyant-wing-4/artifacts/pca9awui_IMG_8210.jpeg";
const RODRIGO_PHOTO   = "https://customer-assets.emergentagent.com/job_flamboyant-wing-4/artifacts/0z5jyt38_IMG_8212.jpeg";
// To wire up the founder video later, replace this with a YouTube or Vimeo
// embed URL (e.g. "https://www.youtube.com/embed/XXXXXXXXXXX"). While null,
// a styled cream placeholder is rendered in its place.
const FOUNDER_VIDEO_EMBED_URL = "https://www.youtube.com/embed/xOOrKVply5k";

const FRANCISCO_BIO = `Almost two years ago I moved back to Mexico. I did it the hard way - scavenger-hunting resources, figuring things out after I needed them, discovering problems I didn't know were coming.

What made it harder: I wasn't moving alone. I moved with Jenna - my American partner who had never lived outside the US - our two year old son, and her mom, who packed up her entire life in America and said yes before Jenna finished asking.

I had 15 years of cross-border real estate experience and deep personal roots in Mexico. And it was still overwhelming. That told me everything.

If it was that hard for us - it was impossible for everyone else doing it without that foundation.

That's why we built Mundalo. The platform we wish had existed. Your personalized Blueprint, your community of people already living it, and everything you need to stop researching and start packing.

Mexico is one of the greatest opportunities available to Americans right now. You shouldn't have to figure it out alone.`;

const RODRIGO_BIO = `Mundalo was born from a deeply personal question: why is moving abroad still so hard? Rodrigo built this platform because he lived the complexity firsthand - and knew it didn't have to be that way.

With 15+ years as an executive in consulting and SaaS, Rodrigo has helped organizations scale across Latin America and the US. Having spent his career as the bridge between two countries and two cultures, he knows what it takes to build something that actually works across borders.

Originally from Mexico City, educated at Tec de Monterrey and the University of Texas, and now based in Dallas with his wife Andrea and their two daughters - Rodrigo brings both the professional toolkit and the lived immigrant perspective to Mundalo.

His mission: give every person who dreams of living abroad the clarity, tools, and community to actually make it happen.`;

const FOUNDERS = [
  {
    name: 'Francisco Rizo',
    role: 'Co-Founder',
    photo: FRANCISCO_PHOTO,
    bio: FRANCISCO_BIO,
    linkedin: 'https://www.linkedin.com/in/josefranciscorizo',
    instagram: 'https://www.instagram.com/franciscorizo_',
    photoSide: 'left',
    testIdKey: 'francisco',
  },
  {
    name: 'Rodrigo Manzanilla',
    role: 'Co-Founder',
    photo: RODRIGO_PHOTO,
    bio: RODRIGO_BIO,
    linkedin: 'https://www.linkedin.com/in/rodrigomanzanilla',
    instagram: 'https://www.instagram.com/manza_travels',
    photoSide: 'right',
    testIdKey: 'rodrigo',
  },
];

const FounderCard = ({ name, role, photo, bio, linkedin, instagram, photoSide, testIdKey }) => {
  const photoEl = (
    <div className="relative" data-testid={`founder-${testIdKey}-photo-wrap`}>
      <div className="aspect-[3/4] w-full overflow-hidden rounded-2xl border border-[#EFE6CF] shadow-[0_4px_24px_rgba(10,31,46,0.08)] bg-[#FCF8EE]">
        <img
          src={photo}
          alt={`${name} - Co-Founder of Mundalo`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="absolute -bottom-3 left-4 sm:left-6 bg-[#F9F6EF] border border-[#EFE6CF] px-3 py-1 rounded-full">
        <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
          {role}
        </p>
      </div>
    </div>
  );

  const textEl = (
    <div data-testid={`founder-${testIdKey}-text`}>
      <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
        Co-Founder
      </p>
      <h2 className="font-display italic text-4xl sm:text-5xl text-[#0A1F2E] leading-none mb-6">
        {name}
      </h2>
      <div className="space-y-4 text-[#0A1F2E]/80 leading-relaxed text-[15px] sm:text-base">
        {bio.split('\n\n').map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-8">
        <a
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A66C2] text-white text-xs font-mono uppercase tracking-[0.12em] hover:bg-[#084d99] transition"
          data-testid={`founder-${testIdKey}-linkedin`}
          aria-label={`${name} on LinkedIn`}
        >
          <Linkedin className="w-3.5 h-3.5" />
          LinkedIn
        </a>
        <a
          href={instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs font-mono uppercase tracking-[0.12em] transition hover:opacity-90"
          style={{
            background:
              'linear-gradient(135deg,#F58529 0%,#DD2A7B 40%,#8134AF 70%,#515BD4 100%)',
          }}
          data-testid={`founder-${testIdKey}-instagram`}
          aria-label={`${name} on Instagram`}
        >
          <Instagram className="w-3.5 h-3.5" />
          Instagram
        </a>
      </div>
    </div>
  );

  return (
    <section
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center"
      data-testid={`founder-card-${testIdKey}`}
    >
      {photoSide === 'left' ? (
        <>
          <div className="md:col-span-5">{photoEl}</div>
          <div className="md:col-span-7">{textEl}</div>
        </>
      ) : (
        <>
          <div className="md:col-span-7 order-2 md:order-1">{textEl}</div>
          <div className="md:col-span-5 order-1 md:order-2">{photoEl}</div>
        </>
      )}
    </section>
  );
};

const About = () => {
  return (
    <div className="bg-[#F9F6EF] min-h-screen flex flex-col">
      <SEO
        title="About Mundalo - Built by people who made the move"
        description="Meet the founders of Mundalo. Built by people who moved to Mexico the hard way, so you don't have to."
        path="/about"
      />
      <Header />

      <main className="flex-1">
        {/* ====== VIDEO (moved to very top - iter 133) ====== */}
        <section
          className="px-6 md:px-10 pt-16 pb-16 sm:pt-20 sm:pb-24"
          data-testid="about-video"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="font-mono uppercase text-[11px] tracking-[0.22em] text-[#1A5C6E] mb-3">
                Hear from the founders
              </p>
              <h2 className="font-display italic text-4xl sm:text-5xl md:text-6xl text-[#0A1F2E] leading-tight">
                Why we built Mundalo
              </h2>
            </div>
            <div className="rounded-2xl border border-[#EFE6CF] bg-white overflow-hidden shadow-[0_8px_40px_rgba(10,31,46,0.08)]">
              <div
                className="relative w-full aspect-video bg-[#0A1F2E]"
                data-testid="about-video-player"
              >
                {FOUNDER_VIDEO_EMBED_URL ? (
                  <iframe
                    src={FOUNDER_VIDEO_EMBED_URL}
                    title="Why we built Mundalo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    data-testid="about-video-iframe"
                  />
                ) : (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
                    style={{
                      backgroundImage:
                        'linear-gradient(135deg, rgba(44,168,160,0.18) 0%, rgba(10,31,46,0.95) 65%)',
                    }}
                    data-testid="about-video-placeholder"
                  >
                    <PlayCircle
                      className="w-16 h-16 sm:w-20 sm:h-20 text-white/85 mb-5"
                      strokeWidth={1.2}
                    />
                    <p className="font-mono uppercase text-[11px] tracking-[0.22em] text-white/70 mb-3">
                      Coming soon
                    </p>
                    <p className="font-display italic text-white text-2xl sm:text-3xl max-w-md leading-snug">
                      “We started Mundalo for the family we used to be.”
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="h-px max-w-6xl mx-auto bg-[#EFE6CF]" />

        {/* ====== HERO ====== */}
        <section
          className="relative pt-16 pb-14 sm:pt-24 sm:pb-20 px-6 md:px-10 overflow-hidden"
          data-testid="about-hero"
        >
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-mono uppercase text-[11px] tracking-[0.22em] text-[#1A5C6E] mb-5">
              The story behind Mundalo
            </p>
            <h1 className="font-display italic text-5xl sm:text-6xl md:text-7xl text-[#0A1F2E] leading-[1.02] mb-7 tracking-tight">
              Built by people who<br className="hidden sm:block" /> made the move.
            </h1>
            <p className="text-[#0A1F2E]/75 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
              Mundalo was born from real experience - and backed by everything we learned the hard way.
            </p>
          </div>
          {/* subtle dotted accent - desktop only; on mobile these were
              overlapping the centered hero text and clipping the headline. */}
          <div className="hidden md:block absolute -top-10 -right-12 w-72 h-72 rounded-full bg-[#FCF8EE] -z-0" />
          <div className="hidden md:block absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#F4ECD8]/60 -z-0" />
        </section>

        <div className="h-px max-w-6xl mx-auto bg-[#EFE6CF]" />

        {/* ====== FOUNDERS ====== */}
        <section className="px-6 md:px-10 py-16 sm:py-24" data-testid="about-founders">
          <div className="max-w-6xl mx-auto space-y-20 sm:space-y-28">
            {FOUNDERS.map((f) => (
              <FounderCard key={f.testIdKey} {...f} />
            ))}
          </div>
        </section>

        <div className="h-px max-w-6xl mx-auto bg-[#EFE6CF]" />

        {/* ====== BOTTOM CTA ====== */}
        <section
          className="px-6 md:px-10 py-20 sm:py-28 text-center"
          data-testid="about-cta"
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display italic text-4xl sm:text-5xl md:text-6xl text-[#0A1F2E] leading-tight mb-7">
              Ready to make your move?
            </h2>
            <p className="text-[#0A1F2E]/70 text-base sm:text-lg mb-9 max-w-xl mx-auto">
              Start free for 15 days. Card at signup. Cancel anytime. See the Community, meet the founders, and decide for yourself.
            </p>
            <Link
              to="/start-trial"
              className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[12px] tracking-[0.15em] font-semibold px-8 py-4 rounded-full transition"
              data-testid="about-cta-start-free"
            >
              Start free - 15 Days
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
